/**
 * Created by jv on 2019-08-20 (13:34).
 */
import utils from "Sources/utils/utils";
import AppError from "Sources/models/app-error";
import defaultLayout from "Sources/components/business-model/layouts/default";
import Sector from "Sources/components/business-model/sector";
import RoadMap from "Sources/components/business-model/road-map";
import PowerGrid from "Sources/components/business-model/power-grid";

const log = require("Sources/utils/logger")();

const fabric = require("fabric").fabric;

class BusinessModel {
  constructor(options) {
    if (utils.isNull(options)) throw new AppError({exception: new Error("Options for BusinessModel cannot be null")});

    this.id = options.id || utils.generateElementId("business-model-canvas-");

    this.containerElement = $(options.containerElement);
    this.availableCanvasSize = options.availableCanvasSize;
    this.onCanvasSizeChanged = options.onCanvasSizeChanged;

    this.layoutData = utils.isNotNull(options.layoutData) ? options.layoutData : defaultLayout;

    this.sectorsGroup = null;
    this.sectors = [];

    this.income = utils.isNumber(options.income) ? options.income : 0;
    this.incomeIconGroup = null;
    this.incomeIconBackground = null;
    this.incomeIconStroke = null;
    this.incomeIconText = null;

    this.faqIcons = [];
    this.faqIconsVisible = utils.isNotNull(options.faqIconsVisible) ? options.faqIconsVisible : false;

    this.canvasRendered = false;
    this.onRenderComplete = options.onRenderComplete;
    this.onFaqClick = options.onFaqClick;

    this.debugGroup = null;

    this.previewRoads = [];
    this.previewSectors = [];
    this.previewPowerlines = [];

    this.activeInvestments = [];

    this.shouldAbortAnimations = false;
  }

  render() {
    log.info("Rendering business model for layout %o", this.layoutData);

    this.createCanvas();

    this.debugGroup = new fabric.Group([], {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      evented: false,
      reference: false,
      movable: false
    });

    this.canvas.add(this.debugGroup);

    this.powerGrid = new PowerGrid({
      businessModel: this,
      onRenderComplete: () => this._checkRenderComplete()
    });

    this.powerGrid.render();

    this.roadMap = new RoadMap({
      businessModel: this,
      onRenderComplete: () => this._checkRenderComplete()
    });

    this.roadMap.render();

    this.sectorsGroup = new fabric.Group([], {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      evented: false,
      reference: false,
      movable: false,

      objectCaching: false
    });

    this.canvas.add(this.sectorsGroup);

    for (let sectorKey in this.layoutData.sectors) {
      if (this.layoutData.sectors.hasOwnProperty(sectorKey)) {
        this.layoutData.sectors[sectorKey].key = sectorKey;

        let sector = new Sector({
          businessModel: this,
          sectorData: this.layoutData.sectors[sectorKey],
          onRenderComplete: () => {
            this.sectorsGroup.addWithUpdate(sector.sectorGroup);

            this._checkRenderComplete();
          }
        });

        sector.render();
        this.sectors.push(sector);
      }
    }

    this.setUpIncomeIcon();

    this.setUpFaqIcons();

    this.setUpAutoResize();
  }

  _checkRenderComplete() {
    if (this.hasRendered()) {
      this.incomeIconGroup.visible = true;

      this.sectorsGroup.addWithUpdate();
      this.sectorsGroup.setCoords();
      this.sectorsGroup.bringToFront();

      this.debugGroup.bringToFront();

      this.setUpCanvasLayer();

      this.canvas.renderAll();

      if (utils.isFunction(this.onRenderComplete)) {
        this.onRenderComplete.call(this);
      }
    }
  }

  hasRendered() {
    let renderCompleted = this.canvasRendered;

    let sectorsRendered = true;

    for (let i = 0; i < this.sectors.length; i++) {
      sectorsRendered = sectorsRendered && this.sectors[i].hasRendered;
      if (!sectorsRendered) break;
    }

    renderCompleted = renderCompleted && sectorsRendered;
    renderCompleted = renderCompleted && utils.isNotNull(this.powerGrid) && this.powerGrid.hasRendered();
    renderCompleted = renderCompleted && utils.isNotNull(this.roadMap) && this.roadMap.hasRendered();

    return renderCompleted;
  }

  createCanvas() {
    log.info("Creating canvas for business model [%s]", this.id);

    if (utils.isNotNull(this.canvasEl)) {
      this.containerElement[0].removeChild(this.canvasEl);
      this.canvas = null;
      this.canvasEl = null;
    }

    this.canvasEl = document.createElement("canvas");
    this.canvasEl.id = this.id;

    this.containerElement.append(this.canvasEl);

    this.canvas = new fabric.Canvas(this.id, {
      backgroundColor: this.layoutData.backgroundColor || "white",

      width: this.layoutData.size.width,
      height: this.layoutData.size.height,

      imageSmoothingEnabled: false
    });

    this.canvas.selection = false;

    if (!utils.isNullOrEmpty(this.layoutData.background)) {
      this.setUpBackground()
        .then(() => {
          this.canvasRendered = true;
          this._checkRenderComplete();
        });
    } else {
      this.canvasRendered = true;
    }

    if (__DEVELOPMENT__) {
      this.addGridToCanvas(50);
    }
  }

  setUpBackground() {
    if (!utils.isNullOrEmpty(this.layoutData.background)) {
      let options = {
        top: 0,
        left: 0,

        evented: false,
        selectable: false,
        hasBorders: false,
        hasControls: false,
        reference: false
      };

      if (this.layoutData.background.endsWith("svg")) {
        return this.loadSvg(this.layoutData.background, options)
          .then((fabricObj) => {
            this.canvas.add(fabricObj);
            this.canvas.sendToBack(fabricObj);

            fabricObj.scaleToWidth(this.canvas.width);
            fabricObj.setCoords();
            this.canvas.renderAll();

            return fabricObj;
          })
          .catch((err) => {
            log.error("Failed to load background image - %o", err);
          });
      } else {
        return this.loadImage(this.layoutData.background, options)
          .then((fabricObj) => {
            this.canvas.add(fabricObj);
            this.canvas.sendToBack(fabricObj);

            fabricObj.scaleToWidth(this.canvas.width);
            fabricObj.setCoords();
            this.canvas.renderAll();

            return fabricObj;
          })
          .catch((err) => {
            log.error("Failed to load background image - %o", err);
          });
      }
    }

    return null;
  }

  setUpIncomeIcon() {
    this.incomeIconBackground = new fabric.Circle({
      radius: this.layoutData.incomeIcon.radius,
      fill: this.layoutData.incomeIcon.backgroundColor,
      left: this.layoutData.incomeIcon.position.x,
      top: this.layoutData.incomeIcon.position.y,
      originX: this.layoutData.incomeIcon.position.originX,
      originY: this.layoutData.incomeIcon.position.originY
    });

    this.incomeIconBackground.setShadow({
      color: "rgba(0, 0, 0, 0.3)",
      blur: 3,
      offsetX: 0,
      offsetY: 0
    });

    this.incomeIconStroke = new fabric.Circle({
      radius: this.layoutData.incomeIcon.innerStrokeRadius,
      fill: "transparent",
      stroke: this.layoutData.incomeIcon.innerStrokeColor,
      strokeWidth: this.layoutData.incomeIcon.innerStrokeWidth,
      left: this.layoutData.incomeIcon.position.x,
      top: this.layoutData.incomeIcon.position.y,
      originX: this.layoutData.incomeIcon.position.originX,
      originY: this.layoutData.incomeIcon.position.originY
    });

    this.incomeIconText = new fabric.Textbox("", {
      width: this.incomeIconBackground.radius * 2,
      originX: "center",
      originY: "center",
      left: this.layoutData.incomeIcon.position.x,
      top: this.layoutData.incomeIcon.position.y,

      fontFamily: this.layoutData.incomeIcon.text.fontFamily,
      fontSize: this.layoutData.incomeIcon.text.fontSize,
      textAlign: this.layoutData.incomeIcon.text.alignment,
      stroke: this.layoutData.incomeIcon.text.color
    });

    this._setIncomeIconText(this.income);

    this.incomeIconGroup = new fabric.Group([this.incomeIconBackground, this.incomeIconStroke, this.incomeIconText], {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      evented: false,
      reference: false,
      movable: false,
      visible: false
    });

    this.canvas.add(this.incomeIconGroup);

    this.setIncomeIconTheme(this.incomeIconTheme, false);
  }

  setUpFaqIcons() {
    for (let i = 0; i < this.layoutData.faqIcons.length; i++) {
      this.createFaqIcon(this.layoutData.faqIcons[i]);
    }
  }

  createFaqIcon(faqIcon) {
    faqIcon = {...faqIcon, ...this.layoutData.faqSettings}; // merging two objects

    let faqIconBackground = new fabric.Circle({
      radius: faqIcon.radius,
      fill: faqIcon.backgroundColor,
      left: faqIcon.position.x,
      top: faqIcon.position.y,
      originX: faqIcon.position.originX,
      originY: faqIcon.position.originY
    });

    faqIconBackground.setShadow({
      color: "rgba(0, 0, 0, 0.3)",
      blur: 3,
      offsetX: 0,
      offsetY: 0
    });

    let faqIconStroke = new fabric.Circle({
      radius: faqIcon.innerStrokeRadius,
      fill: "transparent",
      stroke: faqIcon.innerStrokeColor,
      strokeWidth: faqIcon.innerStrokeWidth,
      left: faqIcon.position.x,
      top: faqIcon.position.y,
      originX: faqIcon.position.originX,
      originY: faqIcon.position.originY
    });
    faqIconStroke.circonopoly = {};
    faqIconStroke.circonopoly.id = "stroke";
    faqIconStroke.circonopoly.markedColor = faqIcon.markedColor;

    let faqIconText = new fabric.Textbox("?", {
      width: faqIconBackground.radius * 2,
      originX: "center",
      originY: "center",
      left: faqIcon.position.x,
      top: faqIcon.position.y,
      // text: "?",

      fontFamily: faqIcon.text.fontFamily,
      fontSize: faqIcon.text.fontSize,
      textAlign: faqIcon.text.alignment,
      stroke: faqIcon.text.color
    });
    faqIconText.circonopoly = {};
    faqIconText.circonopoly.id = "text";
    faqIconText.circonopoly.markedColor = faqIcon.markedColor;

    let faqIconGroup = new fabric.Group([faqIconBackground, faqIconStroke, faqIconText], {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      reference: false,
      movable: false,
      visible: false,
      hoverCursor: "pointer"
    });

    faqIconGroup.circonopoly = {};
    faqIconGroup.circonopoly.faqType = faqIcon.key;

    this.faqIcons.push(faqIconGroup);
    this.canvas.add(faqIconGroup);

    faqIconGroup.on("mousedown", (options) => {
      let faqInfo = {};

      faqInfo.faqType = options.target.circonopoly.faqType;

      let sectorLayoutData = null;

      for (let i = 0; i < this.layoutData.faqIcons.length; i++) {
        if (this.layoutData.faqIcons[i].key === options.target.circonopoly.faqType) {
          sectorLayoutData = this.layoutData.faqIcons[i].sector;
          break;
        }
      }

      if (utils.isNotNull(sectorLayoutData)) {
        for (let i = 0; i < this.sectors.length; i++) {
          if (this.sectors[i].key === sectorLayoutData.key) {
            faqInfo.sector = this.sectors[i];
          }
        }
      }

      if (utils.isFunction(this.onFaqClick)) this.onFaqClick(faqInfo);
    });
  }

  // toggle visibility of FAQ icons
  toggleFaqIcons() {
    this.setFaqIconsVisible(!this.faqIconsVisible)
  }

  setFaqIconsVisible(flag) {
    if (this.faqIconsVisible === flag) return;

    this.faqIconsVisible = flag;

    for (let i = 0; i < this.faqIcons.length; i++) {
      let f = this.faqIcons[i];
      f.visible = this.faqIconsVisible;
    }

    this.canvas.renderAll();
  }

  // change color of faq to green (CMS)
  markFaqIcon(faqType) {
    for (let i = 0; i < this.faqIcons.length; i++) {
      let f = this.faqIcons[i];
      if (f.circonopoly.faqType === faqType) {
        for (let j = 0; j < f.getObjects().length; ++j) {
          let o = f.getObjects()[j];
          if (!utils.isNullOrEmpty(o.circonopoly)) {
            if (o.circonopoly.id === "stroke") {
              o.set({stroke: o.circonopoly.markedColor});
            } else if (o.circonopoly.id === "text") {
              o.set({stroke: o.circonopoly.markedColor});
              o.set({fill: o.circonopoly.markedColor});
            }
          }
        }

        break;
      }
    }
  }

  drawDebugInfo(text, position, color, offset = 0) {
    if (__DEVELOPMENT__ && false) {
      let debugText = new fabric.Textbox(text, {
        originX: "center",
        originY: "center",
        left: position.x + offset,
        top: position.y + offset,

        fontFamily: "Rubik",
        fontSize: 24,
        textAlign: "center",
        stroke: color,
        fill: color
      });

      this.debugGroup.addWithUpdate(debugText);
    }
  }


  setUpCanvasLayer() {
    this.canvas.bringToFront(this.incomeIconGroup);

    for (let i = 0; i < this.faqIcons.length; i++) {
      let f = this.faqIcons[i];
      f.visible = this.faqIconsVisible;
      f.bringToFront();
    }
  }

  getSectorForKey(key) {
    for (let i = 0; i < this.sectors.length; i++) {
      let sector = this.sectors[i];

      if (sector.key === key) {
        return sector;
      }
    }

    log.error("Failed to find sector for key [%s]", key);

    return null;
  }

  loadSvg(source, svgOptions, fromString) {
    return new Promise((resolve, reject) => {
      function onComplete(objects, options) {
        if (utils.isNotNull(svgOptions)) {
          options = {...options, ...svgOptions};
        }

        let obj = fabric.util.groupSVGElements(objects, options);

        obj.originX = options.originX || "left";
        obj.originY = options.originY || "top";
        /*obj.top = options.top;
        obj.left = options.left;*/

        obj.width = options.width || obj.width;
        obj.height = options.height || obj.height;

        // obj.evented = that.evented;
        // obj.setCoords();

        resolve(obj);
      }

      function addSvgProperties(el, obj) {
        obj.id = el.getAttribute("id");
      }

      if (fromString) {
        fabric.loadSVGFromString(source, onComplete, addSvgProperties);
      } else {
        fabric.loadSVGFromURL(source, onComplete, addSvgProperties);
      }
    });
  }

  loadImage(source, options) {
    return new Promise((resolve, reject) => {
      let img = new Image();
      img.src = "";
      img.onload = function () {
        let fabricImage = new fabric.Image(this, options);
        resolve(fabricImage);
      };
      img.onerror = reject;
      img.src = source;
    });
  }

  set income(value) {
    if (!utils.isNumber(value)) {
      value = parseInt(value);
      value = utils.isNumber(value) ? value : 0;
    }

    this._income = value;

    this._setIncomeIconText(this.income);
  }

  get income() {
    return this._income;
  }

  _setIncomeIconText(value) {
    if (utils.isNotNull(this.incomeIconText)) {
      let textValue = value >= 0 ? ("+ " + value) : ("- " + Math.abs(value));
      this.incomeIconText.set({text: textValue});
      this.canvas.renderAll();
    }
  }

  setIncomeIconTheme(theme, inverse) {
    let textColor = this.layoutData.incomeIcon.text.color,
      backgroundColor = this.layoutData.incomeIcon.backgroundColor,
      innerStrokeColor = this.layoutData.incomeIcon.innerStrokeColor;

    if (utils.isNotNull(theme) && this.layoutData.incomeIcon.themes.hasOwnProperty(theme)) {
      this.incomeIconTheme = theme;

      textColor = utils.isNotNull(this.layoutData.incomeIcon.themes[theme].text.color) ? this.layoutData.incomeIcon.themes[theme].text.color : textColor;
      backgroundColor = utils.isNotNull(this.layoutData.incomeIcon.themes[theme].backgroundColor) ? this.layoutData.incomeIcon.themes[theme].backgroundColor : backgroundColor;
      innerStrokeColor = utils.isNotNull(this.layoutData.incomeIcon.themes[theme].innerStrokeColor) ? this.layoutData.incomeIcon.themes[theme].innerStrokeColor : innerStrokeColor;
    } else {
      this.incomeIconTheme = "default";
    }

    if (inverse) {
      let tmp = innerStrokeColor;
      textColor = backgroundColor;
      innerStrokeColor = backgroundColor;
      backgroundColor = tmp;

      this.incomeIconTheme += "_inverse";
    }

    this.incomeIconBackground.set({fill: backgroundColor});
    this.incomeIconStroke.set({stroke: innerStrokeColor});
    this.incomeIconText.set({fill: textColor, stroke: textColor});

    this.canvas.renderAll();
  }

  setCompanyIcon(iconUrl) {
    return this._setSectorActiveIcon("company", iconUrl);
  }

  setCompanyExtraProduct(iconUrl) {
    return this._setSectorActiveIcon("extra_product", iconUrl);
  }

  _setSectorActiveIcon(sectorKey, iconUrl) {
    return new Promise((resolve, reject) => {
      let sector = this.getSectorForKey(sectorKey);

      if (utils.isNull(sector)) {
        reject(new AppError({error: new Error(`[${sector}] sector not found`)}));
      } else {
        return sector.setActiveIcon(iconUrl);
      }
    });
  }

  setUpBeginState() {
    let animationOptions = {instant: true};

    for (let i = 0; i < this.layoutData.startActions.length; i++) {
      let action = this.layoutData.startActions[i];

      if (utils.isNull(action.waitOn) || true)// %NOTE: waitOn needed ?
      {
        doAction.call(this, action);
      }
    }

    function doAction(action) {
      log.info("Executing begin state action: %o", action);

      if (action.targetType === "road") {
        this.roadMap.activateRoad(action.target[0].startPoint, action.target[0].intersections, action.target[0].endPoint, animationOptions);
      } else if (action.targetType === "sector") {
        // noinspection JSPotentiallyInvalidUsageOfClassThis
        this.getSectorForKey(action.target).activate(animationOptions);
      } else if (action.targetType === "power-line") {
        this.powerGrid.activatePowerLines(action.target);
      }
    }
  }

  previewInvestment(investmentType) {
    let animationOptions = {};
    animationOptions.instant = true;
    animationOptions.isPreview = true;
    animationOptions.roadBackgroundColor = "transparent"; //this.layoutData.roadSettings.roadColor;

    animationOptions.sectorStrokeWidthPreview = 2;
    animationOptions.sectorBackgroundColorPreview = this.layoutData.sectorSettings.backgroundColor;

    if (utils.isNotNull(investmentType) && this.layoutData.investmentActions.hasOwnProperty(investmentType)) {
      let investmentActions = this.layoutData.investmentActions[investmentType].actions;
      for (let i = 0; i < investmentActions.length; ++i) {
        doAction.call(this, investmentActions[i]);
      }

      this.canvas.renderAll();
    }

    function doAction(action) {
      if (action.type === "road") {
        animationOptions.roadDividerWidth = this.layoutData.roadSettings.dividerWidth * 2;

        if (!action.active) {
          animationOptions.roadDividerColor = this.layoutData.roadSettings.dividerColorPreviewInactive
        } else {
          // animationOptions.roadBackgroundColor = "#ffffff";
          animationOptions.roadDividerColor = this.layoutData.roadSettings.dividerColorPreviewActive;
        }

        this.previewRoads.push(this.roadMap.previewRoad(action.data.startPoint, action.data.intersections, action.data.endPoint, animationOptions));
      } else if (action.type === "sector") {
        if (!action.active) {
          animationOptions.sectorStrokeColorPreview = this.layoutData.sectorSettings.strokeColorPreviewInactive;
        } else {
          animationOptions.sectorStrokeColorPreview = this.layoutData.sectorSettings.strokeColorPreviewActive;
        }

        // noinspection JSPotentiallyInvalidUsageOfClassThis
        this.previewSectors.push(this.getSectorForKey(action.data.name).preview(animationOptions, true));
      } else if (action.type === "power-line") {
        let color = this.layoutData.powerGridSettings.strokeColorPreviewActive;
        if (!action.active) {
          color = this.layoutData.powerGridSettings.strokeColorPreviewInactive;
        }

        this.previewPowerlines.push(this.powerGrid.previewPowerlines(action.data, color));
      }
    }
  }

  removePreview() {
    this.roadMap.previewGroup.getObjects().forEach((object, key) => {
      this.roadMap.previewGroup.remove(object);
    });

    for (let i = 0; i < this.previewSectors.length; i++) {
      let s = this.previewSectors[i];
      s.hidePreview();
    }

    this.previewPowerlines.forEach((object, key) => {
      object.set({stroke: this.layoutData.powerGridSettings.strokeColor});
    });

    if (this.incomeIconTheme !== "default") {
      this.setIncomeIconTheme();
    }

    this.canvas.renderAll();

    this.previewRoads = [];
    this.previewSectors = [];
    this.previewPowerlines = [];
  }

  applyInvestment(investmentType, animationOptions) {
    this.shouldAbortAnimations = false;

    if (!this.activeInvestments.includes(investmentType)) {
      this.activeInvestments.push(investmentType);
      return this._applyInvestment(investmentType, animationOptions)
    }
  }

  _applyInvestment(investmentType, animationOptions) {
    if (this.shouldAbortAnimations) return;

    async function doAnimation(context, investmentActions) {
      for (let i = 0; i < investmentActions.length; i++) {
        if (context.shouldAbortAnimations) return;

        let action = investmentActions[i];
        let shouldWait = action.wait && !(utils.isNotNull(animationOptions) && animationOptions.instant);

        // Note: if something gets added to animation options inside another method then you should copy the object and assign the new value to the copy!
        //     eg: animationOptions = Object.assign({}, animationOptions, { instant: true });

        // if(!utils.isNullOrEmpty(action.requirement)) continue; // %NOTE: wordt hierbuiten afgechecked

        if (action.type === "icon" && action.active) {
          context.getSectorForKey(action.data.name).setActiveIcon(action.data.source);
        } else if (action.type === "text" && action.active) {
          context.getSectorForKey(action.data.name).setText(action.data.value);
        } else if (action.type === "power-line" && action.active) {
          context.powerGrid.activatePowerLines(action.data);
        } else if (action.type === "power-line" && !action.active) {
          context.powerGrid.deactivatePowerLines(action.data);
        } else if (action.type === "road" && action.active) {
          if (shouldWait) {
            await context.roadMap.activateRoad(action.data.startPoint, action.data.intersections, action.data.endPoint, animationOptions);
          } else {
            context.roadMap.activateRoad(action.data.startPoint, action.data.intersections, action.data.endPoint, animationOptions);
          }
        } else if (action.type === "road" && !action.active) {
          if (shouldWait) {
            await context.roadMap.deactivateRoad(action.data.startPoint, action.data.intersections, action.data.endPoint, animationOptions);
          } else {
            context.roadMap.deactivateRoad(action.data.startPoint, action.data.intersections, action.data.endPoint, animationOptions);
          }
        } else if (action.type === "sector" && action.active) {
          // Don't animate sector activation --> always instant
          let sectorAnimationOptions = Object.assign({}, animationOptions, {instant: true});

          if (shouldWait) {
            // activate is instant at the moment
            await context.getSectorForKey(action.data.name).activate(sectorAnimationOptions);
          } else {
            context.getSectorForKey(action.data.name).activate(sectorAnimationOptions);
          }
        } else if (action.type === "sector" && !action.active) {
          if (shouldWait) {
            await context.getSectorForKey(action.data.name).deactivate(animationOptions);
          } else {
            context.getSectorForKey(action.data.name).deactivate(animationOptions);
          }
        }
      }
    }

    if (utils.isNotNull(investmentType) && this.layoutData.investmentActions.hasOwnProperty(investmentType)) {
      let investmentActions = this.layoutData.investmentActions[investmentType].actions;

      return doAnimation(this, investmentActions);
    }
  }

  setInvestments(investments) {
    // Starts from scratch (and also reapplies the begin state)
    this.reset();
    this.setUpBeginState();
    this.updateInvestments(investments, {
      instant: true
    });
    // this.updateInvestments(investments, null);
  }

  removeAllInvestments(animationOptions) {
    this.roadMap.deactivateAllRoads(animationOptions);
    this.powerGrid.deactivateAllPowerLines(animationOptions);

    for (let i = 0; i < this.sectors.length; i++) {
      this.sectors[i].deactivate(animationOptions);
    }

    this.canvas.renderAll();

    this.activeInvestments = [];
  }

  updateInvestments(investments, animationOptions) {
    this.shouldAbortAnimations = false;

    // Adds to the current business model (!== setInvestments which first clears the current business model)
    if (utils.isNull(investments)) return;

    log.info("Updating business model investments:\nCurrent active investments: %o\nUpdate: %o", this.activeInvestments, investments);

    for (let i = 0; i < investments.length; i++) {
      if (utils.isNotNull(investments[i]) && !this.shouldAbortAnimations) {
        this.applyInvestment(investments[i], animationOptions);
      }
    }
  }

  reset() {
    this.removePreview();
    this.setFaqIconsVisible(false);

    this.abortAnimations();
    this.removeAllInvestments({
      instant: true
    });
  }

  abortAnimations() {
    this.shouldAbortAnimations = true;

    this.powerGrid.abortAnimations();
    this.roadMap.abortAnimations();

    for (let i = 0; i < this.sectors.length; i++) {
      this.sectors[i].abortAnimations();
    }
  }

  addGridToCanvas(gridSize) {
    let width = this.canvas.width,
      height = this.canvas.height;

    let gridGroup = [];

    for (let i = 0; i < (width / gridSize); i++) {
      let x = i * gridSize;

      gridGroup.push(new fabric.Line([x, 0, x, height], {stroke: "#ccc"}));

      gridGroup.push(new fabric.Text(x + "", {
        left: x,
        top: 0,
        textAlign: "center",
        fontSize: 10,
        fontFamily: "Helvetica Nue, Helvetica, Sans-Serif, Arial, Trebuchet MS"
      }));
    }

    for (let i = 0; i < (height / gridSize); i++) {
      let y = i * gridSize;

      gridGroup.push(new fabric.Line([0, y, width, y], {stroke: "#ccc"}));

      gridGroup.push(new fabric.Text(y + "", {
        left: 0,
        top: y,
        textAlign: "center",
        fontSize: 10,
        fontFamily: "Helvetica Nue, Helvetica, Sans-Serif, Arial, Trebuchet MS"
      }));
    }

    this.canvas.add(new fabric.Group(gridGroup, {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      evented: false,
      reference: false,
      movable: false
    }));
  }

  destroy() {
    this.canvas.clear();
    this.canvas.dispose();
    this.canvas = null;
  }

  setUpAutoResize() {
    this.fitToContainer();

    let resizeFunction = utils.debounce(this.fitToContainer, 250, false, this);
    window.addEventListener("resize", resizeFunction, false);
  }

  fitToContainer(e) {
    // Note: using fabric canvas zoom causes blurriness for some reason, manually resize canvas doesn't (not sure if this breaks something else though)
    // Note²: using `imageSmoothingEnabled: false` has an even better result (event with the fabricjs zooming)
    // Best to worst quality (sharpness):
    //    1. imageSmoothingEnabled: false with fabricjs zooming
    //    2. imageSmoothingEnabled: false with manual canvas scaling
    //    3. imageSmoothingEnabled: true (default) with manual canvas scaling
    //    4. imageSmoothingEnabled: true (default) with fabricjs zooming
    let $window = $(window);
    // let containerWidth = Math.min(this.layoutData.size.width, this.containerElement.width());
    // let windowHeight = Math.min(this.layoutData.size.height, $window.height());
    let containerWidth = Math.min(this.layoutData.size.width * 1.5, this.containerElement.width());
    let windowHeight = Math.min(this.layoutData.size.height * 1.5, this.containerElement.height());

    if (utils.isFunction(this.availableCanvasSize)) {
      let availableSize = this.availableCanvasSize(this);
      containerWidth = utils.isNotNull(availableSize.width) ? availableSize.width : containerWidth;
      windowHeight = utils.isNotNull(availableSize.height) ? availableSize.height : windowHeight;
    }

    let scaleRatio = Math.min(containerWidth / this.layoutData.size.width, windowHeight / this.layoutData.size.height);

    log.debug("Resizing canvas to fit container:\nWidth: %f\nHeight: %f\nScale: %f", this.containerElement.width(), this.containerElement.height(), scaleRatio);

    if (scaleRatio > 0) {
      let oldWidth = this.canvas.width,
        oldHeight = this.canvas.height;

      let newWidth = this.layoutData.size.width * scaleRatio,
        newHeight = this.layoutData.size.height * scaleRatio;

      this.canvas.setDimensions({width: newWidth, height: newHeight});
      this.canvas.setZoom(scaleRatio);

      if (utils.isFunction(this.onCanvasSizeChanged)) {
        this.onCanvasSizeChanged(oldWidth, oldHeight, newWidth, newHeight);
      }
    }

    // let initialWidth = this.layoutData.size.width;
    // let initialHeight = this.layoutData.size.height;
    //
    // let $container = this.containerElement;
    //
    // let containerElement = this.containerElement.find(".canvas-container");
    //
    // resizeCanvas($container.find("canvas").get(0));
    // resizeCanvas($container.find("canvas").get(1));
    //
    // function resizeCanvas(canvas)
    // {
    //   let newWidth = null;
    //   let newHeight = null;
    //
    //   let $window = $(window);
    //   let windowWidth = $window.width(),
    //     windowHeight = $window.height(),
    //     windowRatio = windowWidth / windowHeight;
    //
    //   let width = Math.min($container.width(), initialWidth);
    //   let height = initialHeight / initialWidth * width;
    //
    //   let wratio = width / height;
    //   let ratio = initialWidth / initialHeight;
    //
    //   if(windowRatio < wratio)
    //   {
    //     newWidth = width;
    //     newHeight = width / ratio;
    //   }
    //   else
    //   {
    //     height = Math.max(windowHeight, initialHeight);
    //     width = initialWidth / initialHeight * height;
    //
    //     newWidth = width;
    //     newHeight = height;
    //   }
    //
    //   if(newWidth !== null && newHeight !== null)
    //   {
    //     canvas.style.width = newWidth + "px";
    //     canvas.style.height = newHeight + "px";
    //
    //     containerElement.css("width", newWidth + "px");
    //     containerElement.css("height", newHeight + "px");
    //   }
    // }
  }
}

export default BusinessModel;



// WEBPACK FOOTER //
// ./app/js/components/business-model/business-model.js